import React from 'react'
import { observer } from 'mobx-react-lite'
import { MDBIcon, MDBBtn } from 'mdb-react-ui-kit'
import PushNotificationsStore from './../PushNotifications.store'
import PushSelfTest from './../PushSelfTest'
import { IOS_INSTALL_STEPS, IOS_USE_SAFARI_NOTE, IOS_STEPS_AGAIN_NOTE } from './../pushCopy'

import './PushEnableCard.component.scss'

/**
 * The soft-ask.
 *
 * Deliberately a two-step flow: this card explains WHY, and only an explicit
 * click reaches Notification.requestPermission(). A permission prompt fired on
 * page load gets blanket-denied, and a denial is permanent for that browser —
 * there is no second chance, so the first ask has to be earned.
 *
 * Renders nothing unless there is something the agent can actually act on
 * (see PushNotificationsStore.shouldOfferEnable): never while impersonating,
 * never once blocked, never during a recent dismissal.
 */
const PushEnableCard = () => {
  const Store = PushNotificationsStore

  // After a successful enable the soft-ask disappears, but the self-test still
  // needs an answer — so it outlives the card that triggered it.
  if (!Store.shouldOfferEnable)
    return Store.selfTest === 'pending' || Store.selfTest === 'failed' ? <PushSelfTest /> : null

  const isIOS = Store.needsHomeScreenInstall

  return (
    <div id='PushEnableCardComponent' className={isIOS ? 'is-ios' : ''}>
      <div className='push-card-icon' aria-hidden='true'>
        <MDBIcon icon='bell' />
      </div>

      <div className='push-card-body'>
        <h5>{isIOS ? 'Add USABG to your Home Screen to get alerts' : 'Get important alerts from USABG'}</h5>

        {isIOS ? (
          <>
            <p>
              On iPhone and iPad, notifications only work once USABG is saved to your Home Screen. It takes about ten
              seconds:
            </p>
            {Store.browserName !== 'Safari' ? <p className='push-card-note'>{IOS_USE_SAFARI_NOTE}</p> : null}
            <ol>
              {IOS_INSTALL_STEPS.map((step, i) => (
                <li key={`ios-step-${i}`}>{step}</li>
              ))}
            </ol>
            <p className='push-card-note'>{IOS_STEPS_AGAIN_NOTE}</p>
          </>
        ) : (
          <p>
            Turn on notifications and we can reach you about time-sensitive items even when the back office is closed.
            You can turn them off again at any time from your Alerts page.
          </p>
        )}

        <div className='push-card-actions'>
          {!isIOS && (
            <MDBBtn color='indigo' disabled={Store.isBusy} onClick={() => Store.enable()}>
              {Store.isBusy ? 'Working…' : 'Turn On Notifications'}
            </MDBBtn>
          )}
          <button type='button' className='push-card-dismiss' onClick={() => Store.snooze()}>
            {/* "Got it" read as an acknowledgement — people tapped it meaning
                "understood, installing now" and lost the steps. */}
            {isIOS ? 'Remind me later' : 'Not now'}
          </button>
        </div>

        {Store.error ? (
          <p className='push-card-error' role='alert'>
            {Store.error}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export default observer(PushEnableCard)
